// Règles de validation partagées entre frontend et backend
export const VALIDATION_RULES = {
  event: {
    // OBLIGATOIRE POUR PUBLICATION (pas pour brouillon)
    title: {
      required: true,
      minLength: 3,
      maxLength: 100,
      pattern: /^[a-zA-Z0-9\s\-_.,!?()À-ÿ]+$/,
      messageUser: 'Your event needs a catchy title! ✍️',
      messageServer: 'Le titre est requis (3-100 caractères)'
    },
    description: {
      required: true, // Publication seulement
      minLength: 10,
      maxLength: 5000,
      messageUser: 'Add a description to get people excited! 📝',
      messageServer: 'La description est requise (10-5000 caractères)'
    },
    location: {
      required: true, // Publication seulement
      maxLength: 200,
      messageUser: 'Where is your event happening? 📍',
      messageServer: 'La localisation est requise (max 200 caractères)'
    },
    locationName: {
      required: true, // Publication seulement
      maxLength: 100,
      messageUser: 'Give your venue a name people will remember! 🏢',
      messageServer: 'Le nom du lieu est requis (max 100 caractères)'
    },
    
    // COORDONNÉES (invisible pour l'utilisateur)
    coordinates: {
      lat: {
        required: true,
        min: -90,
        max: 90,
        messageUser: 'Please pick a location from the suggestions so your event appears on the map.',
        messageServer: 'Latitude invalide (-90 à 90)'
      },
      lng: {
        required: true,
        min: -180,
        max: 180,
        messageUser: 'Please pick a location from the suggestions so your event appears on the map.',
        messageServer: 'Longitude invalide (-180 à 180)'
      }
    },
    
    // DATES ET HEURES
    datetime: {
      required: true, // Publication seulement
      futureOnly: true,
      messageUser: 'Pick a date for your event! 📅',
      messageServer: 'Date requise et doit être dans le futur'
    },
    time: {
      required: true, // Publication seulement
      messageUser: 'What time does your event start? ⏰',
      messageServer: 'Heure de début requise'
    },
    endTime: {
      required: false, // Optionnel - sera calculé automatiquement (+1h) si non fourni
      messageUser: 'When does your event end? ⌛',
      messageServer: 'Heure de fin requise'
    },
    
    // ÉVÉNEMENTS RÉCURRENTS
    startDate: {
      required: true, // Pour récurrents uniquement
      messageUser: 'When does your event series begin? 📅',
      messageServer: 'Date de début de série requise'
    },
    startTime: {
      required: true, // Pour récurrents uniquement
      messageUser: 'What time do your events start? ⏰',
      messageServer: 'Heure de début de série requise'
    },
    weekDays: {
      required: true, // Pour récurrents hebdomadaires
      messageUser: 'Which days of the week will your event happen? 🗓️',
      messageServer: 'Jours de la semaine requis'
    },
    endDate: {
      required: true, // Si endType === 'date'
      messageUser: 'When does your event series end? 📅',
      messageServer: 'Date de fin de série requise'
    },
    occurrences: {
      required: true, // Si endType === 'occurrences'
      min: 1,
      messageUser: 'How many times will your event repeat? 🔄',
      messageServer: 'Nombre d\'occurrences requis (min 1)'
    },
    
    // OPTIONNELS
    price: {
      min: 0,
      max: 999999999,
      messageUser: 'Price must be between 0 and 999,999,999 💰',
      messageServer: 'Le prix doit être entre 0 et 1,000,000'
    },
    organizerId: {
      required: true,
      pattern: /^[a-zA-Z0-9_-]+$/,
      messageUser: 'Valid organizer required 👤',
      messageServer: 'ID organisateur invalide'
    }
  },
  
  user: {
    email: {
      required: true,
      maxLength: 254,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      messageUser: 'Please enter a valid email address 📧',
      messageServer: 'Email invalide'
    },
    displayName: {
      required: true,
      minLength: 2,
      maxLength: 75,
      pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/,
      messageUser: 'Name should be 2-75 characters with letters only 👤',
      messageServer: 'Nom invalide (2-75 caractères, lettres uniquement)'
    },
    phone: {
      pattern: /^[\+]?[0-9\s\-\(\)]{10,15}$/,
      messageUser: 'Please enter a valid phone number 📱',
      messageServer: 'Format téléphone invalide'
    }
  }
} as const;

// Helper pour créer les schémas Yup depuis les règles
export const createYupSchema = () => {
  const yup = require('yup');
  
  return {
    eventSchema: yup.object({
      title: yup.string()
        .required(VALIDATION_RULES.event.title.messageServer)
        .min(VALIDATION_RULES.event.title.minLength, VALIDATION_RULES.event.title.messageServer)
        .max(VALIDATION_RULES.event.title.maxLength, VALIDATION_RULES.event.title.messageServer)
        .matches(VALIDATION_RULES.event.title.pattern, VALIDATION_RULES.event.title.messageServer),
      
      description: yup.string()
        .required(VALIDATION_RULES.event.description.messageServer)
        .min(VALIDATION_RULES.event.description.minLength, VALIDATION_RULES.event.description.messageServer)
        .max(VALIDATION_RULES.event.description.maxLength, VALIDATION_RULES.event.description.messageServer),
      
      location: yup.string()
        .required(VALIDATION_RULES.event.location.messageServer)
        .max(VALIDATION_RULES.event.location.maxLength, VALIDATION_RULES.event.location.messageServer),
      
      coordinates: yup.object({
        lat: yup.number()
          .required(VALIDATION_RULES.event.coordinates.lat.messageServer)
          .min(VALIDATION_RULES.event.coordinates.lat.min, VALIDATION_RULES.event.coordinates.lat.messageServer)
          .max(VALIDATION_RULES.event.coordinates.lat.max, VALIDATION_RULES.event.coordinates.lat.messageServer),
        lng: yup.number()
          .required(VALIDATION_RULES.event.coordinates.lng.messageServer)
          .min(VALIDATION_RULES.event.coordinates.lng.min, VALIDATION_RULES.event.coordinates.lng.messageServer)
          .max(VALIDATION_RULES.event.coordinates.lng.max, VALIDATION_RULES.event.coordinates.lng.messageServer)
      }),
      
      date: yup.date()
        .required(VALIDATION_RULES.event.datetime.messageServer)
        .min(new Date(), VALIDATION_RULES.event.datetime.messageServer),
      
      price: yup.number()
        .min(VALIDATION_RULES.event.price.min, VALIDATION_RULES.event.price.messageServer)
        .max(VALIDATION_RULES.event.price.max, VALIDATION_RULES.event.price.messageServer),
      
      organizerId: yup.string()
        .required(VALIDATION_RULES.event.organizerId.messageServer)
        .matches(VALIDATION_RULES.event.organizerId.pattern, VALIDATION_RULES.event.organizerId.messageServer)
    }),

    userSchema: yup.object({
      email: yup.string()
        .required(VALIDATION_RULES.user.email.messageServer)
        .email(VALIDATION_RULES.user.email.messageServer)
        .max(VALIDATION_RULES.user.email.maxLength, VALIDATION_RULES.user.email.messageServer),
      
      displayName: yup.string()
        .required(VALIDATION_RULES.user.displayName.messageServer)
        .min(VALIDATION_RULES.user.displayName.minLength, VALIDATION_RULES.user.displayName.messageServer)
        .max(VALIDATION_RULES.user.displayName.maxLength, VALIDATION_RULES.user.displayName.messageServer)
        .matches(VALIDATION_RULES.user.displayName.pattern, VALIDATION_RULES.user.displayName.messageServer),
      
      phone: yup.string()
        .matches(VALIDATION_RULES.user.phone.pattern, VALIDATION_RULES.user.phone.messageServer)
    })
  };
};

// Helper pour valider côté frontend avec distinction brouillon/publication
export const validateForUser = (
  data: any, 
  type: 'event' | 'user',
  mode: 'draft' | 'publish' = 'publish'
): { valid: boolean, errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  if (type === 'event') {
    const eventRules = VALIDATION_RULES.event;
    
    // TOUJOURS REQUIS (même pour brouillons)
    if (!data.title) errors.title = eventRules.title.messageUser;
    
          // REQUIS SEULEMENT POUR PUBLICATION
      if (mode === 'publish') {
        if (!data.description) errors.description = eventRules.description.messageUser;
        if (!data.location) errors.location = eventRules.location.messageUser;
        if (!data.locationName) errors.locationName = eventRules.locationName.messageUser;

        // Coordonnées GPS obligatoires à la publication (PRD fiabilité découverte touriste, item 2).
        // "Valide" = lat/lng numériques, dans les bornes, jamais (0,0), ET confirmées par une
        // sélection explicite (geohash posé uniquement par onPlaceSelect/GPS/carte — jamais par
        // un défaut hérité du profil organisateur, cf. EventForm.tsx). Pas de double message si
        // le champ lieu est déjà en erreur (texte vide) : on ne remonte qu'une seule erreur à la fois.
        if (!errors.location) {
          const { lat: latRule, lng: lngRule } = eventRules.coordinates;
          const lat = data.latitude;
          const lng = data.longitude;
          const isValidLat = typeof lat === 'number' && Number.isFinite(lat) && lat >= latRule.min && lat <= latRule.max;
          const isValidLng = typeof lng === 'number' && Number.isFinite(lng) && lng >= lngRule.min && lng <= lngRule.max;
          const isNullIsland = lat === 0 && lng === 0; // (0,0) = jamais un vrai lieu d'événement
          const isConfirmedSelection = !!data.geohash;

          if (!isValidLat || !isValidLng || isNullIsland || !isConfirmedSelection) {
            errors.location = latRule.messageUser;
          }
        }

        // Validation spécifique selon le type d'événement
        if (data.isRecurring) {
          if (!data.startDate) errors.startDate = eventRules.startDate.messageUser;
          // startTime is required UNLESS daySchedules provides per-day times
          const hasDaySchedules = data.daySchedules && Object.values(data.daySchedules).some(
            (s: any) => s?.start
          );
          if (!data.startTime && !hasDaySchedules) errors.startTime = eventRules.startTime.messageUser;
          // endTime n'est plus obligatoire - sera calculé automatiquement si absent

          if (data.frequency === 'weekly' && (!data.weekDays || data.weekDays.length === 0)) {
            errors.weekDays = eventRules.weekDays.messageUser;
          }

          if (data.endType === 'date' && !data.endDate) {
            errors.endDate = eventRules.endDate.messageUser;
          }

          if (data.endType === 'occurrences' && (!data.occurrences || data.occurrences < 1)) {
            errors.occurrences = eventRules.occurrences.messageUser;
          }
        } else {
          if (!data.date) errors.date = eventRules.datetime.messageUser;
          if (!data.time) errors.time = eventRules.time.messageUser;
          // endTime n'est plus obligatoire - sera calculé automatiquement si absent
        }
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}; 